/** Lock background scroll while a full-screen overlay (e.g. Ant Design Image
 * preview, or any Object Image Processing Viewer/Editor modal) is open.
 * Restores the exact window scroll position and inner scroll-container
 * positions on unlock, with scrollbar-width compensation so the layout does
 * not jump horizontally when the scrollbar disappears.
 *
 * VISTOERA_OBJECT_IMAGE_VIEWER_EDITOR_FULL_MODAL_ISOLATION_P0 (2026-08-29):
 * generalized from the AI Chat / antd Image-preview-only version. Any
 * component that renders a full-viewport Viewer/Editor overlay marks its
 * outermost DOM node with `MODAL_SURFACE_CLASS` (see `useModalScrollLock`)
 * so the shared document-level wheel/touch/key guard below knows to leave
 * gestures *inside* that surface alone (so the surface's own zoom/pan/
 * scroll still works) while still unconditionally blocking anything that
 * would otherwise reach the page behind it. This is "Layer 1" of the two-
 * layer wheel-isolation design; "Layer 2" is each Viewer/Editor's own
 * canvas-level zoom/pan gesture handling (e.g. `DualPaneCutoutEditor`'s
 * native non-passive `wheel` listener). Layer 1 alone is enough to stop
 * background scroll/jitter even for surfaces with no zoom of their own
 * (ShadowEditorModal, TemplateCutoutEditorModal, StageAssetViewer, plain
 * dialogs) — it does not depend on every surface getting its own gesture
 * code right.
 */
import { useEffect } from 'react'

/** Marker class every Object Image Viewer/Editor modal's outermost node
 * (antd `Modal`'s `wrapClassName`, or a custom overlay's root div) must
 * carry so the background guard below recognizes wheel/touch/click targets
 * inside it as "inside an active surface" rather than "the page behind
 * it". Purely a marker — carries no CSS of its own. */
export const MODAL_SURFACE_CLASS = 'vst-modal-surface'

type LockedElementState = {
  el: HTMLElement
  overflow: string
  scrollTop: number
}

type ScrollLockSnapshot = {
  scrollY: number
  bodyPaddingRight: string
  bodyOverflow: string
  bodyPosition: string
  bodyTop: string
  bodyLeft: string
  bodyRight: string
  bodyWidth: string
  bodyOverscroll: string
  htmlOverflow: string
  htmlPaddingRight: string
  htmlOverscroll: string
  lockedElements: LockedElementState[]
}

const INNER_SCROLL_SELECTORS = [
  '.tc-aichat-history-scroll',
  '.tc-aichat-results-body',
  '.tc-aichat-current-input-text',
  '.tc-aichat-provider-body',
]

/** Selectors treated as "inside an active overlay surface" — gestures
 * targeting elements inside any of these are left alone by the background
 * guard (the surface itself, or antd's own machinery, is responsible for
 * them). Includes antd's built-in Image preview (pre-existing use in
 * AIChatPage) plus the generic marker every Object Image Viewer/Editor now
 * carries. */
const ACTIVE_SURFACE_SELECTOR = [
  '.ant-image-preview-wrap',
  '.ant-image-preview-root',
  '.ant-image-preview-operations',
  `.${MODAL_SURFACE_CLASS}`,
].join(', ')

let activeLocks = 0
let snapshot: ScrollLockSnapshot | null = null

function scrollbarWidth(): number {
  return window.innerWidth - document.documentElement.clientWidth
}

function isInsideActiveSurface(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  const surfaces = document.querySelectorAll(ACTIVE_SURFACE_SELECTOR)
  for (let i = 0; i < surfaces.length; i += 1) {
    if (surfaces[i].contains(target)) return true
  }
  return false
}

function preventBackgroundScroll(e: Event): void {
  if (isInsideActiveSurface(e.target)) return
  e.preventDefault()
}

function preventBackgroundScrollKeys(e: KeyboardEvent): void {
  if (isInsideActiveSurface(e.target)) return
  if ([' ', 'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
    e.preventDefault()
  }
}

export function lockPageScroll(): void {
  if (activeLocks === 0) {
    const scrollY = window.scrollY
    const sbw = scrollbarWidth()
    snapshot = {
      scrollY,
      bodyPaddingRight: document.body.style.paddingRight,
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      bodyTop: document.body.style.top,
      bodyLeft: document.body.style.left,
      bodyRight: document.body.style.right,
      bodyWidth: document.body.style.width,
      bodyOverscroll: document.body.style.overscrollBehavior,
      htmlOverflow: document.documentElement.style.overflow,
      htmlPaddingRight: document.documentElement.style.paddingRight,
      htmlOverscroll: document.documentElement.style.overscrollBehavior,
      lockedElements: [],
    }

    document.documentElement.style.overflow = 'hidden'
    document.documentElement.style.overscrollBehavior = 'none'
    if (sbw > 0) document.documentElement.style.paddingRight = `${sbw}px`
    document.body.style.overflow = 'hidden'
    document.body.style.overscrollBehavior = 'none'
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'

    const seen = new Set<HTMLElement>()
    for (const selector of INNER_SCROLL_SELECTORS) {
      document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        if (seen.has(el)) return
        seen.add(el)
        snapshot!.lockedElements.push({
          el,
          overflow: el.style.overflow,
          scrollTop: el.scrollTop,
        })
        el.style.overflow = 'hidden'
      })
    }

    // capture: true so this runs before any component-level listener in the
    // bubble/capture chain gets a chance to *not* call preventDefault; { }
    // passive: false so preventDefault actually has an effect in every
    // browser (Chrome/Safari both default new wheel/touchmove listeners to
    // passive unless told otherwise — see VISTOERA_OBJECT_IMAGE_VIEWER_
    // EDITOR_FULL_MODAL_ISOLATION_P0 section 9).
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false, capture: true })
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false, capture: true })
    document.addEventListener('keydown', preventBackgroundScrollKeys, { capture: true })
  }
  activeLocks += 1
}

export function unlockPageScroll(): void {
  if (activeLocks <= 0) return
  activeLocks -= 1
  if (activeLocks > 0 || !snapshot) return

  document.removeEventListener('wheel', preventBackgroundScroll, { capture: true })
  document.removeEventListener('touchmove', preventBackgroundScroll, { capture: true })
  document.removeEventListener('keydown', preventBackgroundScrollKeys, { capture: true })

  const saved = snapshot
  document.documentElement.style.overflow = saved.htmlOverflow
  document.documentElement.style.paddingRight = saved.htmlPaddingRight
  document.documentElement.style.overscrollBehavior = saved.htmlOverscroll
  document.body.style.overflow = saved.bodyOverflow
  document.body.style.paddingRight = saved.bodyPaddingRight
  document.body.style.position = saved.bodyPosition
  document.body.style.top = saved.bodyTop
  document.body.style.left = saved.bodyLeft
  document.body.style.right = saved.bodyRight
  document.body.style.width = saved.bodyWidth
  document.body.style.overscrollBehavior = saved.bodyOverscroll

  for (const { el, overflow, scrollTop } of saved.lockedElements) {
    el.style.overflow = overflow
    el.scrollTop = scrollTop
  }

  window.scrollTo(0, saved.scrollY)
  snapshot = null
  activeLocks = 0
}

/** Reference-counted, cleanup-safe React binding for `lockPageScroll` /
 * `unlockPageScroll`. Call once per Viewer/Editor/Modal component with that
 * component's own `open`/`visible` boolean — safe to use from any number of
 * simultaneously-mounted surfaces (nested or sibling) because the counter
 * in `lockPageScroll`/`unlockPageScroll` only actually (un)locks on the
 * 0→1 / 1→0 edge. Always pair with `MODAL_SURFACE_CLASS` on the surface's
 * outermost node (e.g. antd `Modal`'s `wrapClassName`) so this surface's
 * own wheel/touch/click gestures keep working while everything else is
 * blocked from reaching the page behind it. */
export function useModalScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined
    lockPageScroll()
    return () => unlockPageScroll()
  }, [active])
}
